import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Loader2, CheckCircle, Clock, Send, Sparkles, Zap, Star, Building } from "lucide-react";
import axios from "axios";

// Use relative path for standalone deployment, fallback to env variable for development
const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const observerRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });

  const contactInfo = [
    {
      icon: Phone,
      title: "WhatsApp",
      value: "+92 335 688 3921",
      link: "https://wa.me/923356883921",
    },
    {
      icon: Mail,
      title: "Email",
      value: "info@nuvexallc.com",
      link: "mailto:info@nuvexallc.com",
    },
    {
      icon: MapPin,
      title: "Office Address",
      value: "Office No. 1, 1st Floor, Ahmed Plaza, Range Rd, Rawalpindi, 46000",
      link: null,
    },
  ];

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".reveal, .slide-left, .slide-right, .zoom-reveal, .flip-in, .blur-in").forEach((el) => {
      observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);

      if (response.data.success) {
        setIsSuccess(true);
        toast.success("Message sent successfully!");
        setFormData({
          name: "",
          company: "",
          email: "",
          phone: "",
          message: "",
        });
      }
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-24 min-h-screen bg-[#0f0f10] relative overflow-hidden">
      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="particle" style={{ top: '10%', left: '5%', animationDelay: '0s' }} />
        <div className="particle" style={{ top: '30%', left: '90%', animationDelay: '2s' }} />
        <div className="particle" style={{ top: '70%', left: '10%', animationDelay: '4s' }} />
        <div className="particle" style={{ top: '80%', left: '85%', animationDelay: '1s' }} />
      </div>
      
      {/* Floating decorations */}
      <div className="absolute top-32 right-10 animate-float opacity-20">
        <Sparkles className="w-10 h-10 text-[#6DB432] animate-spin" style={{ animationDuration: '10s' }} />
      </div>
      <div className="absolute top-1/2 left-5 animate-float opacity-15" style={{ animationDelay: '2s' }}>
        <Zap className="w-8 h-8 text-[#8BC064] heartbeat" />
      </div>
      
      {/* Neon lines */}
      <div className="absolute top-0 left-1/3 w-px h-full bg-gradient-to-b from-transparent via-[#6DB432]/10 to-transparent" />
      <div className="absolute top-0 right-1/3 w-px h-full bg-gradient-to-b from-transparent via-[#8BC064]/10 to-transparent" />
      
      {/* Contact Section */}
      <section className="py-12 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left Column - Contact Info */}
            <div className="slide-left">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#6DB432]/20 flex items-center justify-center icon-spin">
                  <Building className="w-5 h-5 text-[#6DB432]" />
                </div>
                <h2 className="text-3xl font-bold text-white gradient-text-animated">
                  Contact Information
                </h2>
              </div>
              <p className="text-gray-400 mb-10 hover:text-gray-300 transition-colors duration-300">
                Have a project in mind? Reach out to us through any of these channels and we'll respond within 24 hours.
              </p>

              {/* Contact Items */}
              <div className="flex flex-col gap-8 mb-8">
                {contactInfo.map((item, index) => (
                  <div
                    key={item.title}
                    data-testid={`contact-info-${index}`}
                    className="flex items-start gap-4 group magnetic-hover"
                    style={{ animationDelay: `${index * 0.15}s` }}
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#6DB432]/20 border border-[#6DB432]/30 flex items-center justify-center flex-shrink-0 group-hover:bg-[#6DB432]/40 group-hover:scale-110 transition-all duration-500 icon-spin">
                      <item.icon className="w-5 h-5 text-[#6DB432]" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1 group-hover:text-[#8BC064] transition-colors duration-300">{item.title}</p>
                      {item.link ? (
                        <a
                          href={item.link}
                          target={item.link.startsWith("http") ? "_blank" : undefined}
                          rel={item.link.startsWith("http") ? "noopener noreferrer" : undefined}
                          className="text-white font-semibold hover:text-[#6DB432] transition-colors duration-300 underline-grow"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-white font-semibold group-hover:text-[#8BC064] transition-colors duration-300">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Response Time Badge */}
              <div className="inline-flex items-center gap-3 glass-card border border-white/10 rounded-xl px-5 py-3 mb-8 floating-badge">
                <span className="w-3 h-3 rounded-full bg-[#6DB432] animate-pulse"></span>
                <span className="text-gray-300">
                  Average response time: <span className="text-white font-semibold text-glow">20-30 minutes</span>
                </span>
              </div>

              {/* Embedded Map */}
              <div
                data-testid="google-map-inline"
                className="rounded-xl overflow-hidden border border-white/10 hover:border-[#6DB432]/30 transition-all duration-500 pop-out"
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3323.2834084396586!2d73.02049547616265!3d33.59795034158087!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x38df954d6fbf72e3%3A0xdbf64cd86add0255!2sNUVEXA%20LLC!5e0!3m2!1sen!2s!4v1771548206977!5m2!1sen!2s"
                  width="100%"
                  height="220"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="NUVEXA LLC Location"
                ></iframe>
              </div>
            </div>

            {/* Contact Form */}
            <div className="slide-right">
              {isSuccess ? (
                <div className="success-message text-center glass-card border border-[#6DB432]/30 rounded-2xl p-12 bouncy-entrance">
                  <div className="w-20 h-20 rounded-full bg-[#6DB432]/20 flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
                    <CheckCircle className="w-10 h-10 text-[#6DB432] heartbeat" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-4 gradient-text-animated">
                    Message Sent!
                  </h2>
                  <p className="text-gray-400">
                    Thank you for contacting us. Our team will get back to you soon.
                  </p>
                  <Button
                    onClick={() => setIsSuccess(false)}
                    data-testid="send-another-btn"
                    className="mt-6 bg-[#6DB432] hover:bg-[#8BC064] text-white font-bold px-6 py-3 rounded-md btn-glow btn-shine jello-hover"
                  >
                    Send Another Message
                  </Button>
                  
                  {/* Celebration animation */}
                  <div className="mt-6 flex justify-center gap-4">
                    <Sparkles className="w-5 h-5 text-[#6DB432] animate-bounce" />
                    <Star className="w-5 h-5 text-[#8BC064] animate-bounce" style={{ animationDelay: '0.2s' }} />
                    <Sparkles className="w-5 h-5 text-[#6DB432] animate-bounce" style={{ animationDelay: '0.4s' }} />
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-testid="contact-form"
                  className="flip-in glass-card border border-white/5 hover:border-[#6DB432]/20 rounded-xl p-6 sm:p-8 space-y-6 transition-all duration-500"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-[#6DB432]/20 flex items-center justify-center icon-spin">
                      <MessageCircle className="w-5 h-5 text-[#6DB432]" />
                    </div>
                    <h2 className="text-xl font-bold text-white gradient-text-animated">
                      Business Inquiry
                    </h2>
                  </div>
                  <p className="text-gray-400 text-sm mb-6">
                    Fill out the form below and we'll get back to you shortly.
                  </p>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2 group">
                      <Label htmlFor="name" className="text-gray-300 group-hover:text-[#6DB432] transition-colors duration-300">
                        Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="contact-input-name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="form-input bg-[#353537] border-[#7B7B7D]/30 text-white focus:border-[#6DB432] focus:ring-[#6DB432]/20 transition-all duration-300"
                        placeholder="Your name"
                      />
                    </div>
                    <div className="space-y-2 group">
                      <Label htmlFor="company" className="text-gray-300 group-hover:text-[#6DB432] transition-colors duration-300">
                        Company *
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        data-testid="contact-input-company"
                        value={formData.company}
                        onChange={handleChange}
                        required
                        className="form-input bg-[#353537] border-[#7B7B7D]/30 text-white focus:border-[#6DB432] focus:ring-[#6DB432]/20 transition-all duration-300"
                        placeholder="Company name"
                      />
                    </div>
                    <div className="space-y-2 group">
                      <Label htmlFor="email" className="text-gray-300 group-hover:text-[#6DB432] transition-colors duration-300">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="contact-input-email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="form-input bg-[#353537] border-[#7B7B7D]/30 text-white focus:border-[#6DB432] focus:ring-[#6DB432]/20 transition-all duration-300"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div className="space-y-2 group">
                      <Label htmlFor="phone" className="text-gray-300 group-hover:text-[#6DB432] transition-colors duration-300">
                        Phone *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        data-testid="contact-input-phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="form-input bg-[#353537] border-[#7B7B7D]/30 text-white focus:border-[#6DB432] focus:ring-[#6DB432]/20 transition-all duration-300"
                        placeholder="+92 XXX XXXXXXX"
                      />
                    </div>
                  </div>

                  <div className="space-y-2 group">
                    <Label htmlFor="message" className="text-gray-300 group-hover:text-[#6DB432] transition-colors duration-300">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="contact-input-message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="form-input bg-[#353537] border-[#7B7B7D]/30 text-white resize-none focus:border-[#6DB432] focus:ring-[#6DB432]/20 transition-all duration-300"
                      placeholder="Tell us about your project or inquiry..."
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={isSubmitting}
                    className="w-full bg-[#6DB432] hover:bg-[#8BC064] text-white font-bold py-4 rounded-lg btn-glow btn-shine disabled:opacity-50 flex items-center justify-center gap-2 group rubberBand-hover animate-pulse-glow particle-burst"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                        Send Message
                      </>
                    )}
                  </Button>
                  
                  {/* Shimmer effect */}
                  <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#6DB432]/5 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-1000" />
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
